import { useEffect, useRef, useState } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { MACHINE_CATALOG } from '../lib/machines'

interface Props {
  open: boolean
  onClose: () => void
  onScan: (payload: string) => void
  /** DOM id for the camera mount (must be unique if multiple scanners exist). */
  readerId?: string
  /** Show catalog shortcuts (workout scan). Off for machine cataloging. */
  showDemoMachines?: boolean
  title?: string
}

export function QrScannerModal({
  open,
  onClose,
  onScan,
  readerId = 'qr-reader',
  showDemoMachines = true,
  title = 'Scan machine QR',
}: Props) {
  const [error, setError] = useState<string | null>(null)
  const [readingFile, setReadingFile] = useState(false)
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const started = useRef(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setError(null)
    setReadingFile(false)

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)

    const start = async () => {
      try {
        const scanner = new Html5Qrcode(readerId)
        scannerRef.current = scanner
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 8, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (cancelled) return
            onScan(decoded)
            void scanner.stop().catch(() => undefined)
            started.current = false
            onClose()
          },
          () => undefined,
        )
        started.current = true
      } catch (err) {
        scannerRef.current = null
        started.current = false
        const msg = err instanceof Error ? err.message : 'Camera unavailable'
        const hint = showDemoMachines
          ? 'You can upload a photo of the QR code, or pick a known machine.'
          : 'You can upload a photo of the QR code instead.'
        setError(`${msg}. ${hint}`)
      }
    }

    void start()

    return () => {
      cancelled = true
      window.removeEventListener('keydown', onKey)
      const s = scannerRef.current
      if (s && started.current) {
        void s.stop().catch(() => undefined)
        started.current = false
      }
      scannerRef.current = null
    }
  }, [open, onClose, onScan, readerId, showDemoMachines])

  const scanImageFile = async (file: File) => {
    setReadingFile(true)
    setError(null)
    try {
      let scanner = scannerRef.current
      if (scanner && started.current) {
        await scanner.stop().catch(() => undefined)
        started.current = false
      }
      if (!scanner) {
        scanner = new Html5Qrcode(readerId)
        scannerRef.current = scanner
      }
      const decoded = await scanner.scanFile(file, false)
      onScan(decoded)
      onClose()
    } catch {
      setError(
        showDemoMachines
          ? 'Could not read a QR code from that image. Try another photo or pick a demo machine.'
          : 'Could not read a QR code from that image. Try another photo.',
      )
    } finally {
      setReadingFile(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  if (!open) return null

  const fileInputId = `${readerId}-image`

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${readerId}-title`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <h2 id={`${readerId}-title`}>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close scanner">
            X
          </button>
        </header>

        <div id={readerId} className="qr-reader" />

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <div className="stack qr-upload">
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            accept="image/*"
            className="sr-only"
            aria-label="Upload QR photo"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void scanImageFile(file)
            }}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={readingFile}
            onClick={() => fileInputRef.current?.click()}
          >
            {readingFile ? 'Reading image…' : 'Upload QR photo'}
          </button>
          <p className="muted small">Use a photo of the machine sticker if the camera is blocked.</p>
        </div>

        {showDemoMachines ? (
          <div className="known-machines">
            <p className="muted">Demo machines</p>
            <ul>
              {MACHINE_CATALOG.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => {
                      onScan(m.qrPayload)
                      onClose()
                    }}
                  >
                    {m.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  )
}
